import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import api from "@/lib/api";
import { useEntity } from "@/context/EntityContext";

export const TYPE_LABELS = { expense: "Expense", income: "Income", saving: "Saving" };

// iter 72w — CoA type mapping per transaction type. Business workspaces
// filter the Chart-of-Accounts dropdown by these types so an expense txn
// only offers Expense codes, income offers Revenue codes, and saving
// offers Asset/Equity codes.
export const COA_TYPES_FOR_TX = {
  expense: ["Expense"],
  income: ["Revenue"],
  saving: ["Asset", "Equity"],
};

// iter 72{ab} — In Business workspaces the "Type" selector shows the
// standard 5 accounting classifications instead of the Personal-finance
// Expense/Income/Saving trio. Each business kind maps to:
//   - `tx_type`  → the internal transaction type (drives P&L / cash-flow)
//   - `coa`      → which CoA account-types the dropdown offers
// So "Revenue" posts an `income` tx that debits Cash and credits a Revenue
// CoA; "Asset" posts a `saving` tx (balance-sheet reclass) against an
// Asset CoA; "Liability" and "Equity" also post as `saving` (they change
// the balance-sheet without flowing through P&L) and offer their own CoA
// families.
export const BUSINESS_TYPE_OPTIONS = [
  { value: "revenue",   label: "Revenue (Income)", tx_type: "income",  coa: ["Revenue"],
    hint: "Money earned selling products or services" },
  { value: "expense",   label: "Expenses",         tx_type: "expense", coa: ["Expense"],
    hint: "Costs of running daily operations" },
  { value: "asset",     label: "Assets",           tx_type: "saving",  coa: ["Asset"],
    hint: "Resources or items of value owned by the business" },
  { value: "liability", label: "Liabilities",      tx_type: "saving",  coa: ["Liability"],
    hint: "Debts or obligations owed to outside parties" },
  { value: "equity",    label: "Equity (Capital)", tx_type: "saving",  coa: ["Equity"],
    hint: "Owner's stake in the business (assets − liabilities)" },
];

const BUSINESS_KIND_BY_TX = {
  expense: "expense",
  income: "revenue",
  saving: "asset",
};

export default function TransactionForm({ initial, onDone, onCancel, currency }) {
  const [categories, setCategories] = useState({ expense: [], income: [], saving: [] });
  const [accounts, setAccounts] = useState([]);
  const [coaAccounts, setCoaAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    initial?.date ? new Date(initial.date) : new Date()
  );

  const { currentEntity } = useEntity();
  const isBusiness = currentEntity?.workspace_type === "business";

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      type: initial?.type || "expense",
      amount: initial?.amount || "",
      category: initial?.category || "",
      note: initial?.note || "",
      account_id: initial?.account_id || "none",
      coa_code: initial?.coa_code || "none",
      // iter 72{ab} — Business-only "kind" (Revenue / Expense / Asset /
      // Liability / Equity). Personal ignores this. Defaults derived from
      // the internal tx type when editing.
      business_kind: BUSINESS_KIND_BY_TX[initial?.type || "expense"] || "expense",
    },
  });

  const type = watch("type");
  const category = watch("category");
  const accountId = watch("account_id");
  const coaCode = watch("coa_code");
  const businessKind = watch("business_kind");

  useEffect(() => {
    (async () => {
      try {
        const requests = [
          api.get("/categories"),
          api.get("/accounts"),
        ];
        if (isBusiness) requests.push(api.get("/coa"));
        const results = await Promise.all(requests);
        setCategories(results[0].data || { expense: [], income: [], saving: [] });
        setAccounts(results[1].data || []);
        if (isBusiness) setCoaAccounts(results[2].data || []);
      } catch (err) {
        console.warn("[TransactionForm] failed to load categories/accounts/coa:", err?.message);
      }
    })();
  }, [isBusiness]);

  useEffect(() => {
    if (initial) {
      reset({
        type: initial.type,
        amount: initial.amount,
        category: initial.category,
        note: initial.note || "",
        account_id: initial.account_id || "none",
        coa_code: initial.coa_code || "none",
        business_kind: BUSINESS_KIND_BY_TX[initial.type] || "expense",
      });
      setSelectedDate(new Date(initial.date));
    }
  }, [initial, reset]);

  // iter 72w/{ab} — filter CoA options.
  // - Personal: by internal tx type (Expense / Revenue / Asset+Equity).
  // - Business: by the business_kind's CoA family (Revenue, Expense,
  //             Asset, Liability, Equity) so a "Liability" txn only
  //             offers Liability codes, etc.
  const filteredCoa = useMemo(() => {
    if (!isBusiness) return [];
    const kindDef = BUSINESS_TYPE_OPTIONS.find((o) => o.value === businessKind);
    const allowed = kindDef?.coa || COA_TYPES_FOR_TX[type] || [];
    return (coaAccounts || [])
      .filter((c) => c.is_active !== false && allowed.includes(c.type))
      .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  }, [coaAccounts, type, businessKind, isBusiness]);

  const submit = async (values) => {
    setErrorMsg("");
    setSubmitting(true);
    const payload = {
      type: values.type,
      amount: Number(values.amount),
      category: values.category,
      note: values.note || "",
      date: format(selectedDate, "yyyy-MM-dd"),
      currency,
      account_id: values.account_id === "none" ? null : values.account_id,
      coa_code: values.coa_code && values.coa_code !== "none" ? values.coa_code : null,
    };
    try {
      if (initial?.id) {
        await api.patch(`/transactions/${initial.id}`, payload);
      } else {
        await api.post("/transactions", payload);
      }
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      setErrorMsg(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="transaction-form" onSubmit={handleSubmit(submit)} className="space-y-4">
      <div className="space-y-2">
        <Label>{isBusiness ? "Account Type" : "Type"}</Label>
        {isBusiness ? (
          <>
            <Select
              value={businessKind}
              onValueChange={(v) => {
                const def = BUSINESS_TYPE_OPTIONS.find((o) => o.value === v);
                setValue("business_kind", v);
                if (def) {
                  setValue("type", def.tx_type);
                }
                setValue("category", "");
                setValue("coa_code", "none");
              }}
            >
              <SelectTrigger data-testid="tx-type">
                <SelectValue placeholder="Select an account type" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`tx-type-${o.value}`}>
                    <div className="flex flex-col">
                      <span className="font-medium">{o.label}</span>
                      <span className="text-[11px] text-[var(--muted-text)]">{o.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("type")} />
          </>
        ) : (
          <Select
            value={type}
            onValueChange={(v) => {
              setValue("type", v);
              setValue("category", "");
              setValue("coa_code", "none");
            }}
          >
            <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.keys(TYPE_LABELS).map((k) => (
              <SelectItem key={k} value={k} data-testid={`tx-type-${k}`}>{TYPE_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Amount</Label>
          <Input
            id="amount"
            data-testid="tx-amount"
            type="number" step="0.01" min="0"
            {...register("amount", { required: true, min: 0.01 })}
          />
          {errors.amount && <p className="text-xs text-[#E63946]">Enter a valid amount</p>}
        </div>
        <div className="space-y-2">
          <Label>Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                data-testid="tx-date-button"
                type="button"
                variant="outline"
                className="w-full justify-start font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(selectedDate, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="space-y-2">
        <Label>{isBusiness ? "Department" : "Category"}</Label>
        <Select value={category} onValueChange={(v) => setValue("category", v, { shouldValidate: true })}>
          <SelectTrigger data-testid="tx-category">
            <SelectValue placeholder={isBusiness ? "Select a department" : "Select a category"} />
          </SelectTrigger>
          <SelectContent>
            {(categories[type] || []).map((c) => (
              <SelectItem key={c.id} value={c.name} data-testid={`tx-category-${c.name}`}>
                <span className="inline-flex items-center gap-2"><span style={{ color: c.color }}>{c.icon}</span>{c.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" {...register("category", { required: true })} />
        {errors.category && <p className="text-xs text-[#E63946]">{isBusiness ? "Pick a department" : "Pick a category"}</p>}
      </div>

      {isBusiness && (
        <div className="space-y-2" data-testid="tx-coa-field">
          <Label>
            Chart of Accounts
            <span className="ml-2 text-[10px] font-normal text-[var(--muted-text)]">
              posts a Journal Entry under this GL code
            </span>
          </Label>
          <Select
            value={coaCode || "none"}
            onValueChange={(v) => setValue("coa_code", v)}
          >
            <SelectTrigger data-testid="tx-coa-code">
              <SelectValue placeholder="Select an account (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="tx-coa-none">— Skip GL posting —</SelectItem>
              {filteredCoa.length === 0 && (
                <div className="px-3 py-2 text-xs text-[var(--muted-text)]">
                  No active {(COA_TYPES_FOR_TX[type] || []).join(" / ")} accounts. Add one in <b>Chart of Accounts</b>.
                </div>
              )}
              {filteredCoa.map((c) => (
                <SelectItem key={c.id || c.code} value={c.code} data-testid={`tx-coa-${c.code}`}>
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-xs text-[var(--muted-text)]">{c.code}</span>
                    <span>{c.name}</span>
                    <span className="text-[10px] text-[var(--muted-text)]">· {c.type}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label>Account (optional)</Label>
        <Select value={accountId} onValueChange={(v) => setValue("account_id", v)}>
          <SelectTrigger data-testid="tx-account"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" data-testid="tx-account-none">Unassigned</SelectItem>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id} data-testid={`tx-account-${a.id}`}>
                {a.name} · {a.currency}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="note">Note (optional)</Label>
        <Textarea id="note" data-testid="tx-note" rows={3} {...register("note")} />
      </div>

      {/* iter 72{ac} — Journal Entry preview. Mirrors backend
          `_post_transaction_journal()` so the user sees the exact DR/CR
          pair that will hit their ledger. Only appears when a CoA code
          is picked in Business. */}
      {isBusiness && coaCode && coaCode !== "none" && Number(watch("amount")) > 0 && (() => {
        const cfg = BUSINESS_TYPE_OPTIONS.find((o) => o.value === businessKind);
        const coa = (coaAccounts || []).find((c) => c.code === coaCode);
        const wallet = (accounts || []).find((a) => a.id === accountId);
        // Match the backend's wallet CoA resolution: card → 2100 Credit
        // Card Payable, else 1000 Cash.
        const walletLine = wallet?.type === "card"
          ? { code: "2100", label: "Credit Card Payable" }
          : { code: "1000", label: "Cash" };
        const amt = Number(watch("amount")) || 0;
        // Expense & Saving-kinds: DR CoA / CR Wallet.
        // Income (Revenue-kind): DR Wallet / CR CoA.
        const txt = cfg?.tx_type;
        const drCr = txt === "income"
          ? { dr: walletLine, cr: { code: coa?.code, label: coa?.name } }
          : { dr: { code: coa?.code, label: coa?.name }, cr: walletLine };
        const fmt = (n) => `${currency === "USD" ? "$" : currency === "NGN" ? "₦" : ""}${amt.toFixed(2)}`;
        return (
          <div data-testid="je-preview"
               className="rounded-xl border border-[var(--divider)] bg-[var(--card)] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
                Journal Entry Preview
              </div>
              <div className="text-[10px] text-[var(--muted-text)]">
                {cfg?.label || TYPE_LABELS[type]}
              </div>
            </div>
            <div className="font-mono text-xs">
              <div className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-3 py-1"
                   data-testid="je-preview-dr">
                <span className="text-[10px] text-[var(--muted-text)] uppercase">DR</span>
                <span>
                  <span className="text-[var(--muted-text)]">{drCr.dr.code || "—"}</span>
                  <span className="ml-2">{drCr.dr.label || "…"}</span>
                </span>
                <span>{fmt(amt)}</span>
              </div>
              <div className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-3 py-1"
                   data-testid="je-preview-cr">
                <span className="text-[10px] text-[var(--muted-text)] uppercase">CR</span>
                <span className="pl-4">
                  <span className="text-[var(--muted-text)]">{drCr.cr.code || "—"}</span>
                  <span className="ml-2">{drCr.cr.label || "…"}</span>
                </span>
                <span>{fmt(amt)}</span>
              </div>
            </div>
            <div className="text-[10px] text-[var(--muted-text)]">
              Posted automatically when you save. Balances to zero.
            </div>
          </div>
        );
      })()}

      {errorMsg && (
        <p data-testid="tx-error" className="text-xs text-[#E63946]">{errorMsg}</p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button data-testid="tx-cancel" type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          data-testid="tx-submit"
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
        >
          {submitting ? "Saving..." : initial?.id ? "Save changes" : "Add transaction"}
        </Button>
      </div>
    </form>
  );
}
